/**
 * The app's windows: the tmux view's sessions (terminals), Chats, the Pipeline's Chat-and-terminal, commit
 * Diffs, file explorers and the queued prompt being edited, and Global settings anywhere, that stay open side by side, each one a floating window on
 * a desktop, listed in a taskbar along the bottom (`WindowLayer.vue`). On a
 * phone there is room for one, so the one in front fills the screen and the
 * taskbar switches between them.
 *
 * Module state, not a view's: leaving the tmux view and coming back finds the
 * same windows where they were. Not saved — a reload starts with none.
 *
 * One layer draws them at a time — the tmux view's while it is up, else the
 * shell's — and each says which kinds it can draw (`WindowLayer`'s `kinds`):
 * the shell has no session, Chat, Diff or Files to put in one. A phone's tmux view
 * draws no session or queued window either: each is its full-screen sheet there.
 */
import { computed, reactive } from "vue";

export interface ChatWindow {
  kind: "chat";
  repo: string;
  worktree: string | null;
  tag?: string;
}

export interface DiffWindow {
  kind: "diff";
  repo: string;
  hash: string;
  subject: string;
}

/** A tmux session's terminal (TmuxView.vue): the one in front carries the
 *  session's bar and buttons, the others only their terminal. */
export interface SessionWindow {
  kind: "session";
  name: string;
}

/** A queued Claude's prompt, model and worktree, to edit before it is sent
 *  (TmuxView.vue), one window per queued row. The view keeps one draft, so the
 *  one in front is the one being edited; the others show what was saved. */
export interface QueuedWindow {
  kind: "queued";
  id: string;
}

/** The whole server's settings (GlobalSettingsView.vue), admins only. */
export interface SettingsWindow {
  kind: "settings";
}

/** A tree's file explorer and editor (FilesView.vue), one per tree. `path` is the
 *  file to open — a pinned one on arrival — and then the one it has open, which
 *  FilesView writes back (`setFilesPath`) for the pin that is "current". */
export interface FilesWindow {
  kind: "files";
  repo: string;
  worktree: string | null;
  path: string | null;
}

/** A Claude's session as the Pipeline opens it (LiveSessionWindow.vue): its Chat
 *  and its terminal side by side — tabs on a phone. One per session. */
export interface LiveWindow {
  kind: "live";
  repo: string | null;
  worktree: string | null;
  session: string;
  /** Its task's title, for the tab, where the Pipeline knew one. */
  title?: string | null;
}

export type WindowData = SessionWindow | QueuedWindow | ChatWindow | DiffWindow | SettingsWindow | FilesWindow | LiveWindow;
export type WindowKind = WindowData["kind"];

export interface Geom {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Win {
  key: string;
  data: WindowData;
  /** Put away to the taskbar. */
  min: boolean;
  /** Fills the screen (above the taskbar) instead of its `geom`, which is kept for when it is restored. */
  max: boolean;
  /** Stacking order: the highest is in front. */
  z: number;
  geom: Geom;
}

export const MIN_W = 360;
export const MIN_H = 220;
/** What of a window must stay on screen when it is dragged, so it can be got back. */
const KEEP = 80;

const state = reactive({ list: [] as Win[] });
let nextZ = 1;

export const windows = computed((): readonly Win[] => state.list);

/** The one in front among `list`, if any is up. */
export function frontOf(list: readonly Win[]): string | null {
  let best: Win | null = null;
  for (const w of list) if (!w.min && (!best || w.z > best.z)) best = w;
  return best?.key ?? null;
}

/** The window in front, if any is up. */
export const frontKey = computed((): string | null => frontOf(state.list));

export function windowKey(data: WindowData): string {
  if (data.kind === "settings") return "settings";
  if (data.kind === "session") return `session\n${data.name}`;
  if (data.kind === "queued") return `queued\n${data.id}`;
  if (data.kind === "files") return `files\n${data.repo}\n${data.worktree ?? ""}`;
  if (data.kind === "live") return `live\n${data.session}`;
  return data.kind === "chat" ? `chat\n${data.repo}\n${data.worktree ?? ""}` : `diff\n${data.repo}\n${data.hash}`;
}

export function windowTitle(data: WindowData): string {
  if (data.kind === "settings") return "Global settings";
  if (data.kind === "session") return data.name;
  if (data.kind === "queued") return "Queued prompt";
  if (data.kind === "diff") return `${data.hash.slice(0, 7)} ${data.subject}`;
  if (data.kind === "files") return data.worktree ? `${data.repo} ⎇ ${data.worktree}` : data.repo;
  if (data.kind === "live") return data.title || (data.worktree ? `⎇ ${data.worktree}` : data.session);
  return data.worktree ? `⎇ ${data.worktree}` : data.repo;
}

const WIDTHS: Record<WindowKind, number> = { session: 900, queued: 820, chat: 820, diff: 1000, settings: 640, files: 1100, live: 1400 };

function viewportSize(): { vw: number; vh: number } {
  return { vw: window.innerWidth, vh: window.innerHeight };
}

/** Where a new window goes: centred, each one a step down and right of the last
 *  still up, so a second never lands exactly on the first. */
function firstGeom(data: WindowData): Geom {
  const { vw, vh } = viewportSize();
  const w = Math.max(MIN_W, Math.min(WIDTHS[data.kind], vw - 48));
  const h = Math.max(MIN_H, Math.min(Math.round(vh * 0.72), vh - 140));
  const step = state.list.filter((x) => !x.min).length % 8;
  return clampGeom({ x: Math.round((vw - w) / 2) + step * 28, y: 70 + step * 28, w, h });
}

/** Keep enough of a window on screen to grab its title bar again. */
export function clampGeom(g: Geom): Geom {
  const { vw, vh } = viewportSize();
  const w = Math.max(MIN_W, Math.min(g.w, vw));
  const h = Math.max(MIN_H, Math.min(g.h, vh));
  return {
    w,
    h,
    x: Math.min(Math.max(g.x, KEEP - w), vw - KEEP),
    y: Math.min(Math.max(g.y, 0), vh - 60),
  };
}

/** Open one, or — already open — bring it to the front, as it was. */
export function openWindow(data: WindowData): void {
  const key = windowKey(data);
  const had = state.list.find((w) => w.key === key);
  if (had) {
    // A tag's Chat opened again for a worktree that is out now, or the other way round.
    // A tree's Files opened again with no file named keeps the one it has open.
    if (!(data.kind === "files" && data.path === null)) had.data = data;
    focusWindow(key);
    return;
  }
  state.list.push({ key, data, min: false, max: false, z: nextZ++, geom: firstGeom(data) });
}

export function focusWindow(key: string): void {
  const w = state.list.find((x) => x.key === key);
  if (!w) return;
  w.min = false;
  if (frontKey.value !== key) w.z = nextZ++;
}

export function minimizeWindow(key: string): void {
  const w = state.list.find((x) => x.key === key);
  if (w) w.min = true;
}

/** Fill the screen with it, or put it back where it was. */
export function toggleMaximize(key: string): void {
  const w = state.list.find((x) => x.key === key);
  if (!w) return;
  w.max = !w.max;
  focusWindow(key);
}

/** What a window asks before it is closed — the file editor's unsaved changes —
 *  wherever the close comes from, its own × or the taskbar's. */
const closeGuards = new Map<string, () => boolean>();
export function setCloseGuard(key: string, guard: (() => boolean) | null): void {
  if (guard) closeGuards.set(key, guard);
  else closeGuards.delete(key);
}

export function closeWindow(key: string): void {
  if (closeGuards.get(key)?.() === false) return;
  state.list = state.list.filter((w) => w.key !== key);
}

/** The file a Files window has open, kept in its data (FilesWindow). */
export function setFilesPath(key: string, path: string | null): void {
  const w = state.list.find((x) => x.key === key);
  if (w?.data.kind === "files" && w.data.path !== path) w.data = { ...w.data, path };
}

export function moveWindow(key: string, geom: Geom): void {
  const w = state.list.find((x) => x.key === key);
  if (w) w.geom = clampGeom(geom);
}

/** A taskbar button: up and behind → front; in front → put away; put away → back up.
 *  `front` is the one in front of those the taskbar lists, where that is not all. */
export function taskbarClick(key: string, front: string | null = frontKey.value): void {
  const w = state.list.find((x) => x.key === key);
  if (!w) return;
  if (!w.min && front === key) minimizeWindow(key);
  else focusWindow(key);
}

// The shell leaves the taskbar's height free at its foot. Two layers are mounted
// for a moment as the view switches, so the height is up while EITHER has a taskbar.
const taskbars = new Set<symbol>();
export const TASKBAR_H = 34;
export function setTaskbar(layer: symbol, on: boolean): void {
  if (on) taskbars.add(layer);
  else taskbars.delete(layer);
  if (taskbars.size) document.documentElement.style.setProperty("--taskbar-h", `${TASKBAR_H}px`);
  else document.documentElement.style.removeProperty("--taskbar-h");
}

/** Pull every window back on screen after the browser window shrank. */
export function refit(): void {
  for (const w of state.list) w.geom = clampGeom(w.geom);
}

/** For tests: start again with none. */
export function resetWindows(): void {
  state.list = [];
  nextZ = 1;
  closeGuards.clear();
  taskbars.clear();
}
